import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import {
  HeadContent,
  Link,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useRouter,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import AnalyticsListener from "@/components/AnalyticsListener";
import ScrollToTop from "@/components/ScrollToTop";
import WhatsAppWidget from "@/components/WhatsAppWidget";
import { reportVibeError } from "@/lib/vibe-error-reporting";
import NotFound from "@/pages/NotFound";
import appCss from "../styles.css?url";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()(
  {
    head: () => ({
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1.0" },
        { title: "Abacus Design Solutions" },
      ],
      links: [
        {
          rel: "stylesheet",
          href: appCss,
        },
        { rel: "preconnect", href: "https://fonts.googleapis.com" },
        {
          rel: "preconnect",
          href: "https://fonts.gstatic.com",
          crossOrigin: "anonymous",
        },
        {
          rel: "stylesheet",
          href: "https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;600;700;800&display=swap",
        },
        { rel: "preconnect", href: "https://assets.cdn.filesafe.space" },
        { rel: "preconnect", href: "https://vibe.filesafe.space" },
        {
          rel: "icon",
          type: "image/png",
          href: "https://assets.cdn.filesafe.space/TkZSxIjkCDLzrMPXgP9Q/media/6aa3febdbd832ef7bd946034.png",
        },
      ],
      scripts: [
        {
          // Load GTM/GA4 only upon first real user interaction (scroll, touch, click, keydown)
          // or after page becomes completely idle, eliminating early main-thread script bloat.
          children:
            "window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', 'G-NQEENZJWDT'); (function(){function __loadGtag(){if(window.__gtagLoaded)return;window.__gtagLoaded=true;var s=document.createElement('script');s.async=true;s.src='https://www.googletagmanager.com/gtag/js?id=G-NQEENZJWDT';document.head.appendChild(s);}var ev=['pointerdown','scroll','keydown','touchstart'];function trigger(){ev.forEach(function(e){window.removeEventListener(e,trigger,{passive:true})});__loadGtag();}ev.forEach(function(e){window.addEventListener(e,trigger,{passive:true})});})();",
        },
      ],
    }),
    shellComponent: RootShell,
    component: RootComponent,
    notFoundComponent: NotFound,
    errorComponent: RootErrorComponent,
  },
);

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <ScrollToTop />
      <AnalyticsListener />
      <Outlet />
      <WhatsAppWidget />
    </QueryClientProvider>
  );
}

function RootErrorComponent({
  error,
  reset,
}: {
  error: Error;
  reset: () => void;
}) {
  const router = useRouter();

  useEffect(() => {
    console.error(error);
    reportVibeError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-5 py-16 text-foreground">
      <section className="max-w-lg text-center">
        <p className="font-heading text-sm font-bold uppercase tracking-[0.25em] text-accent">
          Something went wrong
        </p>
        <h1 className="mt-4 font-heading text-4xl font-bold sm:text-5xl">
          This page didn&apos;t load
        </h1>
        <p className="mt-4 text-foreground/70">
          Please try again. If the problem continues, return to the home page.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <button
            type="button"
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="rounded-xs bg-primary px-6 py-3 font-heading text-xs font-bold uppercase tracking-wider text-primary-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            Try again
          </button>
          <Link
            to="/"
            className="rounded-xs border border-primary px-6 py-3 font-heading text-xs font-bold uppercase tracking-wider text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
          >
            Go home
          </Link>
        </div>
      </section>
    </main>
  );
}
